import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Instagram, MapPin, Phone } from "lucide-react";
import axios from "axios";
import { CATEGORIES, CONFIG, IMAGES, OFFERS, waLink } from "./config";
import { trackEvent as track } from "./lib/analytics";
import { Chapter, MaskedLines, Parallax, Reveal } from "./motion";
import { Footer, Header, Marquee, WhatsAppButton } from "./components/layout";

// Owner asset slot — renders real photo when configured, else an elegant marked frame.
function AssetSlot({ src, alt, note, sub, className = "", testId }) {
  if (src) return <img loading="lazy" src={src} alt={alt} className={className} data-testid={testId} />;
  return (
    <div className={`asset-slot ${className}`} data-testid={testId}>
      <span className="asset-slot-label">{note}</span>
      <strong>{alt}</strong>
      {sub && <small>{sub}</small>}
    </div>
  );
}

function Hero() {
  const frameRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [4, -4]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-5, 5]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section className="hero">
      <div className="hero-frame-wrap" ref={frameRef} onMouseMove={onMove} onMouseLeave={() => { mx.set(0.5); my.set(0.5); }}>
        <motion.div className="hero-frame" style={{ rotateX, rotateY }} initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}>
          {CONFIG.buildingPhoto ? (
            <img src={CONFIG.buildingPhoto} alt="Hyderabad Shopping Mall building exterior, Nanded" data-testid="hero-building-photo" />
          ) : (
            <AssetSlot
              testId="hero-building-slot"
              note="YOUR BUILDING PHOTO"
              alt="The real Hyderabad Shopping Mall exterior appears here"
              sub="Upload your actual store photograph — this frame is reserved for it."
            />
          )}
          <motion.span className="hero-badge" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}>
            {CONFIG.heroBadge}
          </motion.span>
        </motion.div>
      </div>
      <div className="hero-copy">
        <motion.span className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
          NANDED · FAMILY FASHION DESTINATION
        </motion.span>
        <MaskedLines as="h1" className="hero-title" delay={0.3} lines={[<>FASHION FOR</>, <>EVERY <em>OCCASION</em></>]} />
        <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}>
          Discover sarees, kurtis, suits, party wear, kids fashion and more at Hyderabad Shopping Mall, Nanded.
        </motion.p>
        <motion.div className="hero-buttons" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}>
          <Link data-testid="hero-explore-button" className="button primary" to="/collections">
            Explore Collection <ArrowRight size={16} />
          </Link>
          <a data-testid="hero-directions-button" className="button outline" href={CONFIG.mapUrl} target="_blank" rel="noreferrer" onClick={() => track("directions_click", { source: "hero" })}>
            <MapPin size={16} /> Get Directions
          </a>
        </motion.div>
        <motion.div className="hero-offers" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.25, duration: 0.9 }}>
          {OFFERS.slice(0, 4).map((o, i) => (
            <div key={o.title} data-testid={`hero-offer-${i + 1}`}>
              <b>{o.title}</b>
              <strong>{o.detail}</strong>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Categories() {
  return (
    <section className="section" id="categories">
      <Chapter index="01" eyebrow="FIND YOUR STYLE" title={<>Shop by <em>category</em></>} text="Something for every style, every occasion and every family." />
      <div className="category-grid">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.07}>
            <Link data-testid={`category-card-${i + 1}`} to={`/collections/${c.slug}`} className="arch-card" onClick={() => track("collection_click", { category: c.slug })}>
              <div className="arch-frame">
                <img loading="lazy" src={c.image} alt={`${c.name} fashion collection at Hyderabad Shopping Mall Nanded`} />
                <span className="spotlight" />
              </div>
              <div className="arch-meta">
                <span className="arch-index">{String(i + 1).padStart(2, "0")}</span>
                <h3>{c.name}</h3>
                <p>{c.sub}</p>
                <b>Explore <ArrowRight size={14} /></b>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Offers() {
  return (
    <section className="offers-section">
      <div className="section">
        <div className="offer-head">
          <Chapter index="02" eyebrow="ONLY AT THE MALL" title={<>Today's best <em>offers</em></>} light />
          <Reveal delay={0.2}>
            <Link data-testid="offers-view-all-button" className="button gold" to="/offers" onClick={() => track("offer_click", { source: "home" })}>
              View all offers <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
        <div className="offer-grid">
          {OFFERS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.06}>
              <Link data-testid={`offer-card-${i + 1}`} to="/offers" className="offer-card">
                <span className="offer-num">0{i + 1}</span>
                <h3>{o.title}</h3>
                <strong>{o.detail}</strong>
                <ArrowUpRight size={20} />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Wedding() {
  return (
    <section className="wedding">
      <Parallax className="wedding-bg" speed={0.14}>
        <img loading="lazy" src={IMAGES.wedding} alt="Elegant Indian wedding and festive fashion" />
      </Parallax>
      <div className="wedding-copy">
        <Chapter index="03" eyebrow="CELEBRATE BEAUTIFULLY" title={<>Your wedding. <em>Your style.</em></>} text="Discover beautiful collections for weddings, celebrations and special occasions." light />
        <Reveal className="wedding-links" delay={0.15}>
          {["Wedding Sarees", "Party Wear", "Festive Wear", "Unstitched Suits"].map((x) => (
            <Link data-testid={`wedding-${x.toLowerCase().replaceAll(" ", "-")}-link`} to="/collections/wedding" key={x}>
              {x} <ArrowUpRight size={16} />
            </Link>
          ))}
        </Reveal>
        <Reveal delay={0.25}>
          <Link data-testid="wedding-explore-button" className="button gold" to="/collections/wedding">
            Explore wedding collection <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function NewArrivals() {
  const items = [CATEGORIES[0], CATEGORIES[1], CATEGORIES[3]];
  return (
    <section className="section">
      <Chapter index="04" eyebrow="FRESH FOR YOU" title={<>Just <em>arrived</em></>} text="Discover our latest styles and fresh collections." center />
      <div className="new-grid">
        {items.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.08}>
            <Link data-testid={`new-arrival-${c.slug}`} to="/new-arrivals" className="new-card">
              <img loading="lazy" src={c.image} alt={`Latest ${c.name} collection`} />
              <span>New season · {c.name}</span>
              <b>View collection <ArrowRight size={14} /></b>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal className="center-row">
        <Link data-testid="new-arrivals-all-button" className="text-link" to="/new-arrivals">
          See all new arrivals <ArrowRight size={16} />
        </Link>
      </Reveal>
    </section>
  );
}

function WhyUs() {
  const features = [
    ["WIDE COLLECTION", "Fashion for women, girls and families."],
    ["LATEST STYLES", "Fresh collections and new arrivals."],
    ["SPECIAL OFFERS", "Regular promotions across selected collections."],
    ["WEDDING SHOPPING", "Collections for weddings and celebrations."],
    ["LARGE SHOWROOM", "A convenient destination for family shopping."],
  ];
  return (
    <section className="section why">
      <Chapter index="05" eyebrow="THE HSM DIFFERENCE" title={<>Why shop at <em>Hyderabad Shopping Mall?</em></>} />
      <div className="why-grid">
        {features.map(([h, p], i) => (
          <Reveal key={h} delay={i * 0.06}>
            <div data-testid={`feature-${i + 1}`} className="why-card">
              <span>{String(i + 1).padStart(2, "0")}</span>
              <h3>{h}</h3>
              <p>{p}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Store() {
  const gallery = [0, 1, 2, 3].map((i) => CONFIG.storePhotos[i] || null);
  const layouts = ["tall", "", "", "wide"];
  const alts = ["Store entrance", "Bridal and lehenga display", "Suits display", "Showroom floor"];
  return (
    <section className="section store">
      <div className="store-copy">
        <Chapter index="06" eyebrow="A PLACE TO DISCOVER" title={<>Come shop <em>with us</em></>} text="Take your time, explore every section and find something that feels just right." />
        <Reveal className="address" delay={0.15}>
          <MapPin size={20} />
          <span data-testid="store-address">{CONFIG.address}</span>
        </Reveal>
        <Reveal delay={0.25}>
          <a data-testid="store-visit-button" className="button primary" href={CONFIG.mapUrl} target="_blank" rel="noreferrer" onClick={() => track("directions_click", { source: "store" })}>
            Visit our store <ArrowRight size={16} />
          </a>
        </Reveal>
      </div>
      <div className="store-gallery">
        {gallery.map((photo, i) =>
          photo ? (
            <img key={i} loading="lazy" src={photo} alt={`Hyderabad Shopping Mall ${alts[i] || `store photograph ${i + 1}`}`} data-testid={`store-photo-${i + 1}`} className={layouts[i]} />
          ) : (
            <AssetSlot key={i} testId={`store-photo-slot-${i + 1}`} note="STORE PHOTO" alt="Your real store photograph appears here" className={layouts[i]} />
          )
        )}
      </div>
    </section>
  );
}

function InstagramSection() {
  // Fallback tiles use the owner's real store photos until the live feed is connected.
  const fallbackTiles = [
    "/assets/sarees-display.jpeg",
    "/assets/kurtis-section.webp",
    "/assets/lehenga-display.jpeg",
    "/assets/party-wear.jpeg",
    "/assets/kids-wear.jpeg",
    "/assets/suits-display.jpeg",
  ];
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/instagram/feed`);
        if (active && data.posts?.length) setPosts(data.posts);
      } catch {
        /* static real photos remain */
      }
    };
    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  const api = process.env.REACT_APP_BACKEND_URL;
  const tiles = posts.length
    ? posts.map((p) => ({
        id: p.id,
        image: `${api}/api/instagram/media/${p.id}`,
        href: p.permalink || CONFIG.instagram,
        alt: (p.caption || "Latest Instagram post from Hyderabad Shopping Mall").slice(0, 120),
      }))
    : fallbackTiles.map((im, i) => ({ id: i, image: im, href: CONFIG.instagram, alt: `Latest fashion at Hyderabad Shopping Mall ${i + 1}` }));

  return (
    <section className="section insta">
      <Chapter index="07" eyebrow="FOLLOW ALONG" title={<>Follow our <em>latest styles</em></>} text="See our latest arrivals, offers and fashion inspiration." center />
      <div className="insta-grid">
        {tiles.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.05}>
            <a data-testid={`insta-tile-${i + 1}`} href={t.href} target="_blank" rel="noreferrer" className="insta-tile" aria-label="Open our Instagram profile">
              <img loading="lazy" src={t.image} alt={t.alt} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackTiles[i % fallbackTiles.length]; }} />
              <span className="insta-hover"><Instagram size={22} /></span>
            </a>
          </Reveal>
        ))}
      </div>
      <Reveal className="center-row">
        <a data-testid="instagram-follow-button" className="button outline" href={CONFIG.instagram} target="_blank" rel="noreferrer">
          <Instagram size={16} /> Follow us on Instagram
        </a>
      </Reveal>
    </section>
  );
}

function WhatsAppBanner() {
  return (
    <section className="whatsapp-banner">
      <Reveal>
        <span className="eyebrow">PERSONAL SHOPPING HELP</span>
        <h2>Looking for something <em>specific?</em></h2>
        <p>Message our team on WhatsApp and ask about collections, offers or availability.</p>
        <WhatsAppButton label="Chat on WhatsApp" testId="banner-whatsapp-cta" className="button gold" />
      </Reveal>
    </section>
  );
}

function Location() {
  return (
    <section className="section location">
      <div className="location-copy">
        <Chapter index="08" eyebrow="FIND YOUR WAY HERE" title={<>Visit <em>Hyderabad Shopping Mall</em></>} text={CONFIG.address} />
        <Reveal className="location-actions" delay={0.15}>
          <a data-testid="location-directions-button" className="button primary" href={CONFIG.mapUrl} target="_blank" rel="noreferrer" onClick={() => track("directions_click", { source: "location" })}>
            <MapPin size={16} /> Get directions
          </a>
          <a data-testid="location-call-button" className="button outline" href={`tel:${CONFIG.phone}`} onClick={() => track("call_click", { source: "location" })}>
            <Phone size={16} /> Call us
          </a>
          <WhatsAppButton label="WhatsApp" testId="location-whatsapp-cta" className="button ghost" />
        </Reveal>
      </div>
      <Reveal className="map-frame" delay={0.1}>
        <iframe
          data-testid="google-map-embed"
          title="Hyderabad Shopping Mall location map — Hyderabad Road, Bafna, Nanded"
          src={CONFIG.mapEmbed}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </Reveal>
    </section>
  );
}

function FestivalBanner() {
  const b = CONFIG.festivalBanner;
  if (!b?.enabled || !b.image) return null;
  return (
    <section className="festival-banner">
      <Reveal>
        <Link data-testid="festival-banner-link" to={b.link} onClick={() => track("offer_click", { source: "festival_banner" })}>
          <img loading="lazy" src={b.image} alt={b.alt} />
        </Link>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Header />
      <main>
        <Hero />
        <FestivalBanner />
        <Marquee />
        <Categories />
        <Offers />
        <Wedding />
        <NewArrivals />
        <WhyUs />
        <Store />
        <InstagramSection />
        <WhatsAppBanner />
        <Location />
      </main>
      <Footer />
    </>
  );
}
